"use client";

import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "el-theme";
const THEME_COLOR_LIGHT = "#4A6FA5";
const THEME_COLOR_DARK = "#17171f";

export function applyTheme(theme: "light" | "dark") {
  document.documentElement.classList.toggle("dark", theme === "dark");
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = theme === "dark" ? THEME_COLOR_DARK : THEME_COLOR_LIGHT;
}

/**
 * 明暗主题切换（默认浅色，选择持久化到 localStorage）。
 * 首帧正确性由 layout.tsx 中的内联脚本保证（paint 前加 .dark 类）。
 */
export default function ThemeToggle() {
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // 挂载后从 DOM 读取实际主题（首帧由 layout 内联脚本保证），
    // 这是合法的客户端初始化模式，set-state-in-effect 属误报。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setTheme(document.documentElement.classList.contains("dark") ? "dark" : "light");
    setMounted(true);
  }, []);

  function toggle() {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* 隐私模式下 localStorage 可能不可用 */
    }
    applyTheme(next);
  }

  const isDark = theme === "dark";

  return (
    <button
      onClick={toggle}
      title={isDark ? "切换到浅色模式" : "切换到深色模式"}
      aria-label={isDark ? "切换到浅色模式" : "切换到深色模式"}
      className="relative flex h-11 w-11 sm:h-9 sm:w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent/60 hover:text-foreground"
    >
      <Sun
        className={cn(
          "h-4 w-4 transition-transform duration-300",
          mounted && isDark && "-rotate-90 scale-0"
        )}
      />
      <Moon
        className={cn(
          "absolute h-4 w-4 transition-transform duration-300",
          mounted ? (isDark ? "rotate-0 scale-100" : "rotate-90 scale-0") : "rotate-90 scale-0"
        )}
      />
    </button>
  );
}
